import React, { useContext, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import Pane from 'components/Common/Pane'
import LETVDestinations from '../LETVLandingPage/LETVDestinations'
import { fetchListBanners } from 'actions/ContentActions'
import { BANNER_LOCATION_LE_TV_EPISODE, BANNER_LOCATION_LE_TV_COMPETITION } from 'constants/bannerLocations'
import BannerCarousel from 'components/Common/Banner/BannerCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import HotelOfferCarousel from '../LETVLandingPage/HotelOfferCarousel'
import HostCarousel from '../LETVLandingPage/HostCarousel'
import ImageHero from 'components/Common/ImageHero'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LETVEpisodeHeroOfferCarousel from './LETVEpisodeHeroOfferCarousel'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import { useAppDispatch } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GeoContext from 'contexts/geoContext'
import Error404Page from 'components/Pages/Error404Page'
import { useLETVEpisodes } from 'content/api'
import DreamCarousel from './DreamCarousel'
import EpisodeCarousel from './EpisodeCarousel'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { RouteComponentProps, withRouter } from 'react-router'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PaletteProvider from 'contexts/PaletteProvider'
import EpisodeTabs from './EpisodeTabs'
import { EpisodeIntro } from './EpisodeIntro'
import noop from 'lib/noop'
import { OfferListEventsProvider } from 'components/OfferList/v2/OfferListEventsContext'
import moment from 'moment'
import useQueryParams from 'hooks/useQueryParams'
import OfferCarouselHeader from 'home/components/OfferCarousels/common/OfferCarouselHeader'
import ExperienceOfferCarousel from 'home/components/OfferCarousels/ExperienceOfferCarousel/ExperienceOfferCarousel'
import Places from 'constants/places'

export function getTimeZoneShortString(date: Date) {
  const offsetValue = date.getTimezoneOffset()
  return offsetValue === -600 ? 'AEST' : 'AEDT'
}

function createExperienceHeading(title: string, location: string): JSX.Element {
  return <>{title} <i>{location}</i></>
}

interface Props extends RouteComponentProps<{ episode: string }> {
  episodeBanners: Array<App.ListBanner>;
  competitionBanners: Array<App.ListBanner>;
}

const StyledImageHero = styled(ImageHero)`
  max-height: ${rem(340)};
  height: ${rem(320)};
  position: relative;
`

const ImageHeroPane = styled(Group)`
  padding: ${rem(40)} ${rem(20)};
  z-index: 1;

  ${mediaQueryUp.sm} {
    padding-bottom: ${rem(60)};
  }
`

const Section = styled(Pane)`
  padding-top: ${rem(32)};
  padding-bottom: ${rem(32)};

  ${mediaQueryUp.sm} {
    padding-top: ${rem(40)};
    padding-bottom: ${rem(40)};
  }
`

const WatchTime = styled(Group)`
  ${mediaQueryUp.sm} {
    > * + *::before {
      content: '|';
      padding: 0 ${rem(8)};
    }
  }
`

const EpisodeIntroWrapper = styled(Pane)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(32)};

  ${mediaQueryUp.sm} {
    padding: 0;
  }
`

const OverLay = styled.div`
  background: rgba(0, 0, 0, 0.3);
  position: absolute;
  height: 100%;
  width: 100%;
`

function LETVEpisodePage({ episodeBanners, competitionBanners, match }: Props) {
  const dispatch = useAppDispatch()
  const urlSearchParams = useQueryParams()
  const { preview } = useMemo(() => ({ preview: urlSearchParams.get('preview') }), [urlSearchParams])
  const { currentRegionCode } = useContext(GeoContext)
  const episode = useMemo(() => {
    const episodeString = match.params.episode.split('-')
    return parseInt(episodeString[1]) - 1
  }, [match.params.episode])
  const { data: leTVConfig, isLoading } = useLETVEpisodes(preview === 'true', episode)

  const activeEpisode: App.MarketingLeTVEpisode | undefined = (leTVConfig?.leTVEpisodes ? leTVConfig?.leTVEpisodes[episode] : undefined)
  useEffect(() => {
    dispatch(fetchListBanners(BANNER_LOCATION_LE_TV_EPISODE))
    dispatch(fetchListBanners(BANNER_LOCATION_LE_TV_COMPETITION))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (currentRegionCode !== 'AU' || !leTVConfig?.leTVEpisodes?.length || !activeEpisode || !activeEpisode.isLive) {
    return <Error404Page invalidRegion />
  }

  return (
    <>
      <StyledImageHero id={activeEpisode.heroImage.id!} fit="center">
        <PaletteProvider type="inverse">
          <ImageHeroPane direction="vertical" horizontalAlign="center" gap={16}>
            <Group direction="vertical" gap={32}>
              <Group direction="vertical" gap={8}>
                <div>
                  <Heading variant="heading1" align="center">
                    Luxury Escapes:
                  </Heading>
                  <Heading variant="heading1" align="center">
                    The <i>World's Best</i> Holidays
                  </Heading>
                </div>
                <Heading variant="heading6" align="center">
                  Season 6
                </Heading>
              </Group>
              <WatchTime horizontalAlign="center" direction="vertical" smDirection="horizontal" smVerticalAlign="center">
                <BodyText variant="medium">
                  {moment(activeEpisode.startDate).format('Do MMMM YYYY')} • {moment(activeEpisode.startDate).format('dddd h:mma')} {getTimeZoneShortString(new Date(activeEpisode.startDate))}
                </BodyText>
                <Group horizontalAlign="center" direction="horizontal" verticalAlign="center" gap={4}>
                  <BodyText variant="medium" as="span" align="center">Watch or stream &nbsp;</BodyText>
                  <svg height="12" viewBox="0 0 118 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.5207 5.80691L16.1913 0.794857H0.5V24.1626H6.17815V15.7546H13.1885L14.8592 10.7425H6.16723V5.80691H14.5207ZM29.3384 0.50003C24.4902 0.489111 20.1005 3.41554 18.2442 7.89254C16.377 12.3695 17.4034 17.5345 20.8321 20.9741C24.2609 24.4138 29.4149 25.4402 33.9028 23.5948C38.3907 21.7385 41.3171 17.3707 41.3281 12.5115C41.3171 5.89427 35.9666 0.521869 29.3384 0.50003ZM29.3384 18.7902C26.7942 18.8011 24.4902 17.2615 23.5074 14.9138C22.5247 12.5661 23.0706 9.84714 24.8724 8.04542C26.6741 6.2437 29.3821 5.70864 31.7407 6.68048C34.0884 7.66323 35.6281 9.95633 35.6172 12.5115C35.6172 15.9839 32.7999 18.7902 29.3384 18.7902ZM61.147 0.805777H54.8027L50.828 7.18277L46.8315 0.805777H40.4873L47.6614 12.2057L40.1488 24.1626H46.493L50.839 17.2833L55.1412 24.1626H61.4855L54.0056 12.2057L61.147 0.805777ZM61.6493 5.80691H68.5613V24.1626H74.2395V5.80691H80.2452V0.794857H64.7832L61.6493 5.80691ZM97.3561 5.80691L99.0268 0.794857H83.3354V24.1626H97.3561L99.0268 19.1506H89.0136V14.6845H96.0239L97.6946 9.67242H89.0136V5.80691H97.3561ZM107.839 19.1506V0.794857H102.161V24.1626H116.181L117.863 19.1506H107.839Z" fill="white" />
                  </svg>
                  <ResponsiveImage sizes="24px" id="nfj12z2f1i8akoqx2sb" />
                </Group>
              </WatchTime>
            </Group>
            <BodyText variant="small">Brought to you by Citi Credit Cards</BodyText>
          </ImageHeroPane>
        </PaletteProvider>
        <OverLay />
      </StyledImageHero>
      <PaletteProvider type="inverse">
        <EpisodeIntroWrapper>
          <LayoutContainer size="sm">
            <EpisodeTabs activeEpisode={activeEpisode} leTVEpisodes={leTVConfig?.leTVEpisodes} />
          </LayoutContainer>
          <LayoutContainer>
            <EpisodeIntro activeEpisode={activeEpisode} />
          </LayoutContainer>
        </EpisodeIntroWrapper>
      </PaletteProvider>
      <LETVEpisodeHeroOfferCarousel title={activeEpisode?.featuredCarousel?.title!} header={activeEpisode?.featuredCarousel?.location!} filters={activeEpisode?.featuredCarousel?.filters as App.OfferListFilters} />
      {competitionBanners && (
        <Section>
          <LayoutContainer size="lg">
            <BannerCarousel banners={competitionBanners} onClickOpenNewTab />
          </LayoutContainer>
        </Section>
      )}
      {activeEpisode.exploreMoreOfferCarousels?.map(destination => {
        return <Section key={destination.location}>
          {!destination.filters?.offerTypes?.includes('experience') && <Group direction="vertical" gap={20}>
            <LayoutContainer size="lg">
              <OfferCarouselHeader allPath={destination.url ?? '/'}>
                {destination.title} <i>{destination.location}</i>
              </OfferCarouselHeader>
            </LayoutContainer>
            <HotelOfferCarousel
              filters={destination.filters as App.OfferListFilters}
            />
          </Group>}
          {destination.filters?.offerTypes?.includes('experience') && <ExperienceOfferCarousel showFilters={false} place={Places.Anywhere} ExperienceHeading={createExperienceHeading(destination.title, destination.location)} campaigns={destination.filters.campaigns} />}
        </Section>
      })}
      <Pane>
        <DreamCarousel articles={activeEpisode.dreamBlockArticles!} />
      </Pane>
      <Pane>
        <EpisodeCarousel leTVEpisodes={leTVConfig.leTVEpisodes} />
      </Pane>
      {episodeBanners && (
        <Section>
          <LayoutContainer size="lg">
            <BannerCarousel banners={episodeBanners} onClickOpenNewTab />
          </LayoutContainer>
        </Section>
      )}
      <Section>
        <HostCarousel hosts={leTVConfig.allHosts} />
      </Section>
      <Section>
        <OfferListEventsProvider onListEvent={noop}>
          <LETVDestinations />
        </OfferListEventsProvider>
      </Section>
      <Section>
        <LayoutContainer size="lg">
          <Group direction="vertical" gap={8}>
            <Heading variant="heading6">Terms & Conditions</Heading>
            <BodyText variant="small" colour="neutral-three">National Australia Bank Limited (ABN 12 004 044 937, AFSL and Australian Credit Licence 230686) ("NAB") is the credit provider and issuer of Citi branded financial and credit products. NAB has acquired the business relating to these products from Citigroup Pty Ltd (ABN 88 004 325 080, AFSL and Australian Credit Licence 238098) ("Citi") and has appointed Citi to provide transitional services. Our/us/we means NAB unless the context otherwise requires it. “Citi", "Citibank", "Citigroup", the Arc design and all similar trade marks and derivations thereof are used temporarily under licence by NAB from Citigroup Inc. and related group entities.</BodyText>
          </Group>
        </LayoutContainer>
      </Section>
    </>
  )
}

const mapStateToProps = (state: App.State) => {
  return ({
    episodeBanners: state.content.listBanners?.leTVEpisode,
    competitionBanners: state.content.listBanners?.leTVCompetition,
  })
}

export default withRouter(connect(mapStateToProps)(LETVEpisodePage))
